import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  secondaryHref?: string;
  onSecondaryAction?: () => void;
  tip?: string;
  iconTone?: 'blue' | 'amber' | 'slate';
}

const TONES = {
  blue: { ring: 'bg-blue-50', icon: 'text-blue-400' },
  amber: { ring: 'bg-amber-50', icon: 'text-amber-500' },
  slate: { ring: 'bg-slate-100', icon: 'text-slate-400' },
} as const;

export default function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  secondaryLabel,
  secondaryHref,
  onSecondaryAction,
  tip,
  iconTone = 'blue',
}: EmptyStateProps) {
  const tone = TONES[iconTone];
  return (
    <div className="py-16 text-center">
      <div className={`w-16 h-16 rounded-full ${tone.ring} flex items-center justify-center mx-auto mb-4`}>
        <Icon className={`w-8 h-8 ${tone.icon}`} />
      </div>
      <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto mt-1">{description}</p>
      {tip && (
        <p className="text-xs text-blue-500 max-w-sm mx-auto mt-2 bg-blue-50 px-3 py-1.5 rounded-lg inline-block">
          💡 {tip}
        </p>
      )}
      {(actionLabel || secondaryLabel) && (
        <div className="flex items-center justify-center gap-3 mt-5">
          {actionLabel && actionHref ? (
            <Link
              to={actionHref}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              {actionLabel}
            </Link>
          ) : actionLabel && onAction ? (
            <button
              onClick={onAction}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              {actionLabel}
            </button>
          ) : null}
          {secondaryLabel && secondaryHref ? (
            <Link
              to={secondaryHref}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              {secondaryLabel}
            </Link>
          ) : secondaryLabel && onSecondaryAction ? (
            <button
              onClick={onSecondaryAction}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              {secondaryLabel}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
